import type { ReactElement, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

import { IconCircleTickFilled } from '@xing-com/icons';
import { List } from '@xing-com/list';
import { scale110 } from '@xing-com/tokens';
import { BodyCopy, Headline, Hero, Meta } from '@xing-com/typography';

import * as Styled from './perk-list-mobile.styled';

export type PerkListMobileGroup = {
  heading: string;
  perks: readonly string[];
};

export type PerkListMobilePerks = {
  intro: string;
  groups: readonly PerkListMobileGroup[];
};

export type PerkListMobileProps = {
  action: string;
  activationUrl?: string;
  cancellation: string;
  isShown: boolean;
  onClose: () => void;
  perks: PerkListMobilePerks;
  previousPrice: ReactNode;
  price: ReactNode;
  title: string;
};

type ActionGroupContentProps = Pick<
  PerkListMobileProps,
  'action' | 'activationUrl' | 'cancellation'
>;

const ActionGroupContent = ({
  action,
  activationUrl,
  cancellation,
}: ActionGroupContentProps): ReactElement => (
  <>
    <Styled.ActivationButton
      href={activationUrl}
      size="large"
      variant="primary"
    >
      {action}
    </Styled.ActivationButton>
    <Styled.Cancellation>
      <Meta size="xsmall" lineHeight="dense" noMargin>
        {cancellation}
      </Meta>
    </Styled.Cancellation>
  </>
);

export const PerkListMobile = ({
  action,
  activationUrl,
  cancellation,
  isShown,
  onClose,
  perks,
  previousPrice,
  price,
  title,
}: PerkListMobileProps): ReactElement => {
  const [actionGroupRef, isActionGroupInView] = useInView({
    fallbackInView: true,
    initialInView: true,
  });

  return (
    <>
      <Styled.ModalPage
        closeButton={{ onClick: onClose }}
        focusTrapProps={{ active: false }}
        isAnimated
        isShown={isShown}
        noGrid
        noMargin
      >
        <Styled.Content>
          <Styled.ProductHeader>
            <Styled.ProductSummary>
              <Hero as="h2" size="medium" noMargin>
                {title}
              </Hero>
              <Styled.Pricing>
                <BodyCopy size="xlarge" noMargin>
                  {price}
                </BodyCopy>
                <BodyCopy size="small" lineHeight="dense" noMargin>
                  {previousPrice}
                </BodyCopy>
              </Styled.Pricing>
            </Styled.ProductSummary>
            <BodyCopy size="large" noMargin>
              {perks.intro}
            </BodyCopy>
            <Styled.ActionGroup ref={actionGroupRef}>
              <ActionGroupContent
                action={action}
                activationUrl={activationUrl}
                cancellation={cancellation}
              />
            </Styled.ActionGroup>
          </Styled.ProductHeader>

          <Styled.Groups data-qa="perk-list-mobile">
            {perks.groups.map((group) => (
              <Styled.Group key={group.heading}>
                <Headline as="h3" noMargin size="xlarge">
                  {group.heading}
                </Headline>
                <List>
                  {group.perks.map((perk) => (
                    <Styled.Perk key={perk}>
                      <IconCircleTickFilled
                        aria-hidden="true"
                        height={scale110}
                        width={scale110}
                      />
                      <BodyCopy as="span" noMargin size="medium">
                        {perk}
                      </BodyCopy>
                    </Styled.Perk>
                  ))}
                </List>
              </Styled.Group>
            ))}
          </Styled.Groups>
        </Styled.Content>
      </Styled.ModalPage>
      <Styled.ActionBanner
        display="fixed"
        show={isShown && !isActionGroupInView}
        showCloseButton={false}
        variant="soft"
      >
        <Styled.ActionGroup>
          <ActionGroupContent
            action={action}
            activationUrl={activationUrl}
            cancellation={cancellation}
          />
        </Styled.ActionGroup>
      </Styled.ActionBanner>
    </>
  );
};
